import type { Action } from '@evisort/eds/types/Action';
import type { IntlShape } from 'react-intl';

import chunk from 'lodash/chunk';
import isEqual from 'lodash/isEqual';
import Papa from 'papaparse';

import type { LocalFieldOption } from '~/api/k2/typedefs.autogen';
import type {
  ClientFieldResource,
  FieldSectionRelatedResource,
  FieldSectionResource,
} from '~/api/unified-fields/typedefs.autogen';
import type { DurationPeriod } from '~/features/unified-fields/DurationInput';
import type { K2ReferenceEntityKey } from '~/features/unified-fields/k2ReferenceEntities';
import type { UfFieldValue } from '~/features/unified-fields/questionInputRegistry';
import type { TextInputType } from '~/features/unified-fields/textInputSettings';
import type { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import type {
  ClientField,
  CreateFieldOptionsApiArg,
  CreateFieldOptionsApiResponse,
  FieldOption,
  LogicalType,
} from '~/redux/api/unified-fields/enhancements';

import { URL_REGEXP } from '~/common/constants/regexp';
import { CurrencyType } from '~/eds/src/enums/CurrencyType';
import { PERIODS } from '~/features/unified-fields/DurationInput';
import {
  formatStoredNumber,
  type NumberDisplaySettings,
  type NumberFormat,
} from '~/features/unified-fields/numberDisplaySettings';
import { formatDuration } from '~/features/unified-fields/read-components/DurationReadComponent';
import { formatAddress } from '~/features/unified-fields/read-components/formatAddress';
import { formatMonetaryFieldValue } from '~/features/unified-fields/ufValueFormatters';
import { DATE_ONLY_REGEX, validateEmail } from '~/utils/strings';

type FieldSectionType = 'SYSTEM' | 'USER';

export type FieldSection = (
  | FieldSectionResource
  | FieldSectionRelatedResource
) & {
  attributes: {
    section_type?: FieldSectionType;
  };
};

export type FieldSectionSearchResult = {
  section: FieldSection;
  matchingFieldIds?: string[];
};

export type FieldStatusFilter = 'active' | 'inactive';

const DEFAULT_FIELD_VERSION = 1;

export const FIELD_DESCRIPTION_MAX_LENGTH = 2000;

// Shared left-column width for every row-style field label in the Add/Edit
// Field forms (Field Name, Section, Field Type, Internal API ID, Description,
// Options' Entries/Answer entry/Set default, Advanced Settings' Document
// Types/Show on upload, Edit's Used in Workflows) -- wide enough to fit the
// longest of them ("Used in Workflows", the widest) on one line.
// NameField/SectionField/TypeField use EDS 2.0 FormField's own `layout="row"`
// mode, whose default label width (the `--eds--size-96` token, 96px -- too
// narrow for these labels) AddFieldFormContent/EditFieldFormContent override
// to this same value via a CSS custom property scoped to the form, so every
// row lines up without changing that token's default anywhere else FormField
// is used. The remaining rows hand-roll their own matching column instead of
// going through FormField, so they just read this constant directly.
export const LABEL_COLUMN_WIDTH = 128;

/** Page size for the option-set values list's server-side pagination. */
export const OPTION_SET_PAGE_SIZE = 10;

/** Page size for the default-value picker's incremental "load more" — matches the backend's page[limit] cap. */
export const DEFAULT_VALUE_OPTIONS_PAGE_SIZE = 100;

/** Coerces optimistic-lock version from a resource's meta for PATCH/DELETE requests. */
export const getFieldVersion = (field: {
  meta?: Record<string, unknown> | null;
}): number => {
  const rawVersion = field.meta?.version;
  if (rawVersion == null) {
    return DEFAULT_FIELD_VERSION;
  }

  const version = Number(rawVersion);
  if (!Number.isInteger(version) || version < DEFAULT_FIELD_VERSION) {
    return DEFAULT_FIELD_VERSION;
  }

  return version;
};

/** Same coercion as `getFieldVersion`, named for readability at section call sites. */
export const getSectionVersion = getFieldVersion;

/**
 * Hands the section's existing `field_section_position` values to its fields
 * in their new order, so the numbering scheme the API uses (gaps included)
 * survives a reorder. Falls back to 1..n when any field has no position yet.
 */
export function reassignFieldPositions(
  fields: { attributes: { field_section_position?: number | null } }[],
  orderedIds: string[],
) {
  const existing = fields.map(
    (field) => field.attributes.field_section_position,
  );
  const hasEveryPosition = existing.every(
    (position): position is number => position != null,
  );
  const slots = hasEveryPosition
    ? [...existing].sort((left, right) => left - right)
    : orderedIds.map((_, index) => index + 1);

  return new Map(orderedIds.map((id, index) => [id, slots[index]]));
}

/** getFieldSection's query args — shared so callers hit the same cache entry. */
export const getFieldSectionCacheArg = (sectionId: string) => ({
  sectionId,
  include: 'client_fields' as const,
});

/**
 * Resolves a section reorder from a full before/after id order — the shape the
 * list column's drag-end callback reports, which names only the final order
 * rather than an explicit active/over pair. Finds the single id whose removal
 * makes both orders agree on everything else (the one that moved), then
 * rebuilds the reordered list and that section.
 */
export const resolveSectionMoveFromOrder = (
  sections: FieldSection[],
  orderedIds: string[],
): {
  reordered: FieldSection[];
  movedSection: FieldSection;
} | null => {
  const byId = new Map(sections.map((section) => [section.id, section]));
  const oldOrder = sections.map((section) => section.id);
  if (oldOrder.length !== orderedIds.length) return null;

  const movedId = oldOrder.find((id) => {
    const oldWithout = oldOrder.filter((otherId) => otherId !== id);
    const newWithout = orderedIds.filter((otherId) => otherId !== id);
    return oldWithout.every((value, index) => value === newWithout[index]);
  });
  if (!movedId) return null;

  const movedSection = byId.get(movedId);
  if (!movedSection) return null;

  const reordered = orderedIds.flatMap((id) => {
    const section = byId.get(id);
    return section ? [section] : [];
  });

  return { reordered, movedSection };
};

/** Resolves the latest field record from a list by id (e.g. after cache refetch). */
export const resolveFieldById = (
  fields: ClientField[],
  fieldId: string | null,
): ClientField | null =>
  fieldId ? (fields.find((field) => field.id === fieldId) ?? null) : null;

/**
 * Finds an unused default section name, starting the guess at
 * `existingNames.length + 1` and incrementing past any collision. A guess
 * based on count alone can repeat a name still in use by another section —
 * e.g. delete "Section 1" from a two-section list, then add a new section:
 * count-based naming would re-mint "Section 2", which is already taken.
 */
export const getNextAvailableSectionName = (
  existingNames: string[],
  formatName: (sectionNumber: number) => string,
): string => {
  const taken = new Set(existingNames);
  let sectionNumber = existingNames.length + 1;
  let name = formatName(sectionNumber);
  while (taken.has(name)) {
    sectionNumber += 1;
    name = formatName(sectionNumber);
  }
  return name;
};

/**
 * Field identifiers assigned to a section, from the `client_fields`
 * relationship on a `?include=client_fields` list response. Only
 * `FieldSectionRelatedResource` carries `relationships`; a plain
 * `FieldSectionResource` (no include) has none.
 */
export const getSectionFieldRefs = (section: FieldSection) => {
  if (!('relationships' in section)) return [];

  const data = section.relationships.client_fields?.data;
  if (data == null) return [];
  return Array.isArray(data) ? data : [data];
};

export const getSectionFieldCount = (section: FieldSection): number =>
  getSectionFieldRefs(section).length;

export const isDefaultFieldSection = (section: FieldSection): boolean =>
  Boolean(section.attributes.is_default);

export const isSystemFieldSection = (section: FieldSection): boolean =>
  section.attributes.section_type === 'SYSTEM';

export const resolveDeleteDestinationSectionId = (
  sections: FieldSection[],
  deletingSectionId: string,
): string | null => {
  const destinations = sections.filter(
    (section) => section.id !== deletingSectionId,
  );
  const defaultDestination = destinations.find(isDefaultFieldSection);
  return defaultDestination?.id ?? destinations[0]?.id ?? null;
};

export const searchFieldSections = ({
  sections,
  fields,
  query,
  status,
}: {
  sections: FieldSection[];
  fields: ClientFieldResource[];
  query: string;
  status?: FieldStatusFilter | null;
}): FieldSectionSearchResult[] => {
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const fieldsById = new Map(fields.map((field) => [field.id, field]));
  const matchesQuery = (value: string | null | undefined) =>
    value?.toLocaleLowerCase().includes(normalizedQuery) ?? false;

  return sections.flatMap((section) => {
    const sectionMatchesQuery = matchesQuery(section.attributes.name);
    if (!status && sectionMatchesQuery) return [{ section }];

    const matchingFieldIds = getSectionFieldRefs(section).flatMap(
      ({ id: fieldId }) => {
        const field = fieldsById.get(fieldId);
        if (!field) return [];

        const isInactive = Boolean(field.attributes.deactivated_at);
        if (status === 'active' && isInactive) return [];
        if (status === 'inactive' && !isInactive) return [];

        const { api_name: apiName, custom_name: customName } = field.attributes;
        return sectionMatchesQuery ||
          matchesQuery(customName) ||
          matchesQuery(apiName)
          ? [fieldId]
          : [];
      },
    );

    return matchingFieldIds.length > 0 ? [{ section, matchingFieldIds }] : [];
  });
};

/**
 * Smart fields are integration-managed fields owned by Evisort itself (as
 * opposed to a third-party integration). Unlike other integration-managed
 * fields, they can be edited — except their name, which the integration owns.
 */
export const isSmartField = (field: ClientField): boolean =>
  field.attributes.integration_field_mappings.some(
    (mapping) => mapping.namespace === 'evisort',
  );

/**
 * Mapping domains that get a source chip, in the lowercase form we compare
 * against. This is the single source of truth for which domains are recognized:
 * `FieldSourceChip` keys a total `Record` off the exported `FieldSourceDomain`,
 * so adding a domain here won't compile until it also has a label and color.
 */
const FIELD_SOURCE_DOMAINS = ['standardai', 'customai'] as const;

export type FieldSourceDomain = (typeof FIELD_SOURCE_DOMAINS)[number];

export const FIELD_SOURCE_DOMAIN_STANDARD_AI = FIELD_SOURCE_DOMAINS[0];
export const FIELD_SOURCE_DOMAIN_CUSTOM_AI = FIELD_SOURCE_DOMAINS[1];

const isFieldSourceDomain = (
  domain: string | undefined,
): domain is FieldSourceDomain =>
  FIELD_SOURCE_DOMAINS.some((candidate) => candidate === domain);

/**
 * Structural shape common to `ClientField` and the autogen `ClientFieldResource`.
 * The two field-manager tabs are typed against different resources, and the
 * autogen one leaves `integration_field_mappings` optional.
 */
export type FieldWithIntegrationMappings = {
  attributes: {
    integration_field_mappings?: { domain?: string | null }[];
  };
};

export const getFieldSourceDomains = (
  field: FieldWithIntegrationMappings,
): FieldSourceDomain[] => {
  const domains = new Set<FieldSourceDomain>();

  for (const mapping of field.attributes.integration_field_mappings ?? []) {
    const domain = mapping.domain?.toLowerCase();
    if (isFieldSourceDomain(domain)) {
      domains.add(domain);
    }
  }

  return FIELD_SOURCE_DOMAINS.filter((domain) => domains.has(domain));
};

export const getFieldSourceDomain = (
  field: FieldWithIntegrationMappings,
): FieldSourceDomain | null => getFieldSourceDomains(field)[0] ?? null;

/**
 * Field types added in the M2 milestone. Creating them only needs
 * FlagType.UnifiedFieldsM1 (see AddFieldPanel.tsx's type dropdown filter) —
 * unlike the M2 gate on value-shape features below, which applies to every
 * type except TEXT (see `requiresM2ForValueFeatures`).
 */
export const M2_NEW_FIELD_TYPES = new Set([
  'EMAIL',
  'URL',
  'DURATION',
  'DEPARTMENT',
  'ADDRESS',
]);

/**
 * Whether a type's Preset options/option-set editor and Allow multiple
 * values require `isM2ValueFeaturesEnabled` (FlagType.UnifiedFieldsM1) —
 * every type except TEXT, which has shipped both unconditionally since
 * before M2 existed and stays that way.
 */
const requiresM2ForValueFeatures = (
  logicalType: string | null | undefined,
): boolean => logicalType !== 'TEXT';

/**
 * Field types that can hold more than one value, and so expose the
 * top-level "Allow multiple values" selection (AdvancedSettings'
 * `supportsSelectionMode`) — independent of whether the type also supports
 * an option set (`supportsOptionSetEditor`). Boolean stays single-value (a
 * yes/no field can't be multiple); User/Department/File are multi-value but
 * have no curated option list; Reference is out of scope (EKP-59205).
 *
 * Requires `isM2ValueFeaturesEnabled` for every type except TEXT — see
 * `requiresM2ForValueFeatures`.
 */
export const supportsMultipleValues = (
  logicalType: string | null | undefined,
  isM2ValueFeaturesEnabled = true,
): boolean => {
  if (requiresM2ForValueFeatures(logicalType) && !isM2ValueFeaturesEnabled) {
    return false;
  }
  return (
    logicalType === 'TEXT' ||
    logicalType === 'FILE' ||
    logicalType === 'USER' ||
    logicalType === 'NUMBER' ||
    logicalType === 'DATE' ||
    logicalType === 'MONETARY' ||
    logicalType === 'EMAIL' ||
    logicalType === 'URL' ||
    logicalType === 'DEPARTMENT' ||
    logicalType === 'ADDRESS' ||
    logicalType === 'DURATION'
  );
};

/**
 * TEXT, EMAIL, NUMBER, DATE, URL, MONETARY, ADDRESS, and DURATION fields may
 * attach predefined values via an option set in the field manager.
 *
 * Requires `isM2ValueFeaturesEnabled` for every type except TEXT — see
 * `requiresM2ForValueFeatures`.
 */
export const supportsOptionSetEditor = (
  logicalType: string | null | undefined,
  supportsOptionSet = true,
  isM2ValueFeaturesEnabled = true,
): boolean => {
  if (requiresM2ForValueFeatures(logicalType) && !isM2ValueFeaturesEnabled) {
    return false;
  }
  return (
    (logicalType === 'TEXT' ||
      logicalType === 'EMAIL' ||
      logicalType === 'NUMBER' ||
      logicalType === 'DATE' ||
      logicalType === 'URL' ||
      logicalType === 'MONETARY' ||
      logicalType === 'ADDRESS' ||
      logicalType === 'DURATION') &&
    supportsOptionSet
  );
};

/**
 * TEXT/EMAIL/NUMBER/DATE options are single strings/scalars that fit the
 * single-column CSV pipeline; URL/MONETARY/ADDRESS/DURATION are compound
 * types that use the multi-column pipeline instead (separate named columns
 * per field, e.g. `Amount`/`Currency`) — see `parseCsvFile`,
 * `getFieldValuesCsvTemplate`.
 */
export const supportsCsvImport = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'EMAIL' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE' ||
  logicalType === 'URL' ||
  logicalType === 'MONETARY' ||
  logicalType === 'ADDRESS' ||
  logicalType === 'DURATION';

/** "Free-form entry" (no option set) vs "Preset options" (option set) — the choice AdvancedSettings exposes for option-set-capable fields. */
export type ValueMode = 'free-form' | 'preset';

/**
 * An option-set-capable field left on Free-form entry never gets an option set
 * or its related settings (`allowAddingNewOptions`), no matter what's left
 * over in form state from a prior visit to Preset options — AdvancedSettings
 * intentionally preserves that state across mode switches so a user doesn't
 * lose their work. `allowMultipleValues` is passed through unchanged: since
 * EKP-59142, "Allow multiple values" is a top-level setting independent of
 * the option-set editor, so a free-form field can still be multi-value.
 */
export const resolveValueModeSettings = (
  logicalType: string | null | undefined,
  valueMode: ValueMode,
  allowMultipleValues: boolean,
  allowAddingNewOptions: boolean,
  isM2ValueFeaturesEnabled = true,
) => {
  const isFreeForm =
    supportsOptionSetEditor(logicalType, true, isM2ValueFeaturesEnabled) &&
    valueMode === 'free-form';
  return {
    isFreeForm,
    allowMultipleValues,
    allowAddingNewOptions: !isFreeForm && allowAddingNewOptions,
  };
};

/**
 * Preset options with nothing configured — no values and no way to get any
 * later — would leave a picklist that can never have any options (or, for a
 * field that already has an option set attached, is already handled by the
 * auto-delete-when-empty logic on submit instead of needing to block here).
 */
export const isIncompletePresetField = ({
  valueMode,
  supportsOptionSet,
  hasExistingOptionSet,
  hasValues,
  allowAddingNewOptions,
}: {
  valueMode: ValueMode;
  supportsOptionSet: boolean;
  hasExistingOptionSet: boolean;
  hasValues: boolean;
  allowAddingNewOptions: boolean;
}): boolean =>
  valueMode === 'preset' &&
  !hasExistingOptionSet &&
  supportsOptionSet &&
  !hasValues &&
  !allowAddingNewOptions;

/** TEXT, NUMBER, DATE, MONETARY, EMAIL, URL, ADDRESS, and DURATION fields support the allow_adding_new_options setting. */
export const supportsAllowAddingNewOptions = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE' ||
  logicalType === 'MONETARY' ||
  logicalType === 'EMAIL' ||
  logicalType === 'URL' ||
  logicalType === 'ADDRESS' ||
  logicalType === 'DURATION';

/** Only REFERENCE fields expose the "which entity does this search" picker. */
export const supportsReferenceEntity = (
  logicalType: string | null | undefined,
): boolean => logicalType === 'REFERENCE';

export const buildCustomSettingsAttributes = (
  logicalType: string | null | undefined,
  allowMultipleValues: boolean,
  allowAddingNewOptions = false,
  referenceEntity?: K2ReferenceEntityKey,
  numberFormat?: NumberFormat,
  inputType?: TextInputType,
) => {
  const settings: Record<string, boolean | string> = {};

  // `isM2ValueFeaturesEnabled: true` here is a payload-shape check, not UI
  // gating: this must reflect the type's true capability regardless of
  // FlagType.UnifiedFieldsM1, so allow_multiple_values is always sent
  // explicitly below rather than left to the backend's per-type default.
  if (supportsMultipleValues(logicalType, true)) {
    // Always sent explicitly (never omitted) — Unified Fields defaults
    // allow_multiple_values to `true` for some of these types (e.g.
    // EMAIL/URL/DEPARTMENT) when the key is omitted, which would silently
    // create a multi-value field the toggle shows as single. Sending the
    // toggle's actual value (false by default on create) overrides that.
    settings.allow_multiple_values = allowMultipleValues;
  }

  if (supportsAllowAddingNewOptions(logicalType)) {
    settings.allow_adding_new_options = allowAddingNewOptions;
  }

  if (supportsReferenceEntity(logicalType) && referenceEntity) {
    settings.reference_entity = referenceEntity;
  }

  if (logicalType === 'NUMBER' && numberFormat) {
    settings.number_format = numberFormat;
  }

  if (logicalType === 'TEXT' && inputType) {
    settings.input_type = inputType;
  }

  return Object.keys(settings).length > 0 ? { custom_settings: settings } : {};
};

/**
 * An option's value: a plain string for scalar types (TEXT/EMAIL/NUMBER/
 * DATE), or the compound wire-shape object for compound types (URL:
 * `{value, label}`; MONETARY: `{amount, currency}`). Always already in wire
 * shape — never a widget's local editing state — so it can go straight into
 * a `field_options` create payload.
 */
export type OptionDraftValue = string | Record<string, unknown>;

/**
 * Whether a draft option value counts as "nothing entered yet" — true for a
 * blank/whitespace-only string (covers both a genuinely-empty scalar input
 * and `''`, the sentinel OptionValueInput emits for an incomplete compound
 * value), false for any populated string or compound object.
 */
export const isEmptyOptionDraftValue = (value: OptionDraftValue): boolean =>
  typeof value === 'string' ? !value.trim() : false;

/** Trims a scalar draft value before staging/saving; compound values pass through unchanged. */
export const trimOptionDraftValue = (
  value: OptionDraftValue,
): OptionDraftValue => (typeof value === 'string' ? value.trim() : value);

/**
 * Converts a persisted `field_options.value` (always an object on the wire,
 * even for scalar types — `{"value": "Active"}`) into the `OptionDraftValue`
 * shape the rest of the option-editor pipeline uses: a bare string for
 * scalar types (matching what a fresh add/edit produces), or the object
 * unchanged for compound types (URL, MONETARY, ADDRESS, DURATION — there's
 * no unwrapped form for these, the stored object already *is* the draft
 * shape). Inverse of `createFieldOptionsInChunks`'s own
 * `{value} -> {value: {value}}` wrapping.
 */
export const parseOptionDraftValue = (
  logicalType: string | null | undefined,
  storedValue: Record<string, unknown>,
): OptionDraftValue =>
  logicalType === 'MONETARY' ||
  logicalType === 'URL' ||
  logicalType === 'ADDRESS' ||
  logicalType === 'DURATION'
    ? storedValue
    : String(storedValue.value ?? '');

/**
 * Human-readable display text for an option value — used for the option
 * list row, search, and (via `getFieldOptionLabel`) existing persisted
 * options. Mirrors the backend's own per-type `compute_display_text` so the
 * admin UI stays consistent with it.
 *
 * `formatNumber` (from `useFormatNumber`) drives MONETARY's locale-aware
 * currency formatting via `formatMonetaryFieldValue`, and `intl` drives
 * DURATION's pluralized "N days"/"N weeks" formatting via `formatDuration`
 * — the same formatters the document read components use, so an option
 * reads the same way here as it does everywhere else that value is
 * displayed. Callers that only need a stable label for matching/tracking
 * (default-option bookkeeping, not anything shown to a user) can omit
 * either — MONETARY/DURATION then fall back to a plain, unformatted
 * `"amount currency"`/`"amount period"` string.
 */
export const getOptionDisplayText = (
  logicalType: string | null | undefined,
  value: OptionDraftValue | null | undefined,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
  numberDisplay?: NumberDisplaySettings,
): string => {
  if (value == null) return '';
  if (logicalType === 'NUMBER') {
    const raw =
      typeof value === 'string' ? value : (value as { value?: unknown }).value;
    if (numberDisplay?.numberFormat === 'PERCENT') {
      return formatStoredNumber(raw, numberDisplay) ?? '';
    }
    return String(raw ?? '');
  }
  if (typeof value === 'string') return value;
  if (logicalType === 'MONETARY') {
    const { amount, currency } = value as {
      amount?: unknown;
      currency?: unknown;
    };
    if (amount == null || currency == null) return '';
    if (!formatNumber) return `${amount} ${currency}`;
    return formatMonetaryFieldValue(
      [{ amount, currency } as UfFieldValue],
      formatNumber,
    );
  }
  if (logicalType === 'URL') {
    const { value: url, label } = value as { value?: unknown; label?: unknown };
    // A labeled link has to show both parts in the option list — `label (url)`
    // — so neither the typed label nor the destination is dropped. Unlabeled
    // rows stay as just the URL.
    return label ? `${label} (${url ?? ''})` : String(url ?? '');
  }
  if (logicalType === 'ADDRESS') {
    return formatAddress(value as UfFieldValue);
  }
  if (logicalType === 'DURATION') {
    const { amount, period } = value as { amount?: unknown; period?: unknown };
    if (amount == null) return '';
    if (!intl) return `${amount} ${period}`;
    return formatDuration(intl, value as UfFieldValue) ?? '';
  }
  return String((value as { value?: unknown }).value ?? '');
};

export const getFieldOptionLabel = (
  option: FieldOption,
  logicalType?: string | null,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
  numberDisplay?: NumberDisplaySettings,
): string =>
  getOptionDisplayText(
    logicalType,
    option.attributes.value as OptionDraftValue,
    formatNumber,
    intl,
    numberDisplay,
  );

/** An existing option's value, parsed from its wire shape into `OptionDraftValue` — the compound-aware (URL/ADDRESS/MONETARY/DURATION) counterpart to `getFieldOptionLabel`'s scalar-only cast. */
export const getExistingOptionValue = (
  logicalType: string | null | undefined,
  option: FieldOption,
): OptionDraftValue =>
  parseOptionDraftValue(
    logicalType,
    option.attributes.value as Record<string, unknown>,
  );

/** Filters `existingOptions` down to what's still there after this session's staged deletions (or an empty list, once "delete all" is chosen). */
export const filterExistingOptions = (
  existingOptions: FieldOption[],
  deletedOptionIds: string[],
  deleteAllExistingOptions: boolean,
): FieldOption[] =>
  deleteAllExistingOptions
    ? []
    : existingOptions.filter((o) => !deletedOptionIds.includes(o.id));

/** The whole option list a local dropdown's edit sends to k2: the existing options in the order given, renamed in place and without this session's staged deletions, followed by the new values, with none marked as the default. */
export const buildLocalFieldOptions = ({
  existingOptions,
  deletedOptionIds,
  deleteAllExistingOptions,
  renamedOptionValues,
  newValues,
}: {
  existingOptions: FieldOption[];
  deletedOptionIds: string[];
  deleteAllExistingOptions: boolean;
  renamedOptionValues: Record<string, OptionDraftValue>;
  newValues: OptionDraftValue[];
}): LocalFieldOption[] => [
  ...filterExistingOptions(
    existingOptions,
    deletedOptionIds,
    deleteAllExistingOptions,
  ).map((option) => ({
    value:
      option.id in renamedOptionValues
        ? toFieldOptionValue(renamedOptionValues[option.id])
        : (option.attributes.value as LocalFieldOption['value']),
  })),
  ...newValues.map((value) => ({ value: toFieldOptionValue(value) })),
];

/** The label the edit form tracks a local dropdown's default answer by through renames and deletions: the first item of its `default_value`, or `''` when it has none. */
export const getLocalDefaultOptionLabel = (field: ClientField | null) => {
  const [storedDefault] =
    (field?.attributes.default_value?.data as
      | Record<string, unknown>[]
      | undefined) ?? [];
  return field?.attributes.option_set_id && storedDefault
    ? toDefaultTrackingLabel(
        parseOptionDraftValue(field.attributes.logical_type, storedDefault),
      )
    : '';
};

/** The `default_value` a local field's save sends once its options change: `undefined` while every stored default is still one of `optionValues`, the value among `changedValues` (the added and renamed ones) that `defaultLabel` was renamed to, or `null` once the default's option is gone. */
export const resolveLocalFieldDefaultValue = ({
  storedValues,
  optionValues,
  defaultLabel,
  changedValues,
}: {
  storedValues: Record<string, unknown>[];
  optionValues: Record<string, unknown>[];
  defaultLabel: string | null;
  changedValues: OptionDraftValue[];
}) => {
  if (
    storedValues.every((stored) =>
      optionValues.some((value) => isEqual(value, stored)),
    )
  ) {
    return undefined;
  }
  const renamedTo = changedValues.find(
    (value) => toDefaultTrackingLabel(value) === defaultLabel,
  );
  return renamedTo === undefined
    ? null
    : { data: [toFieldOptionValue(renamedTo)] };
};

const CSV_ROW_LIMIT = 10_000;
const CREATE_OPTIONS_CHUNK_SIZE = 2_000;

type CreateFieldOptionsTrigger = (arg: CreateFieldOptionsApiArg) => {
  unwrap: () => Promise<CreateFieldOptionsApiResponse>;
};

/** The value Unified Fields stores for an option entered in the form. */
export const toFieldOptionValue = (value: OptionDraftValue) =>
  typeof value === 'string' ? { value } : value;

/**
 * Uploads field options in 2k-row chunks. All but the last chunk fire in
 * parallel (cache invalidation skipped); the last fires after to trigger
 * exactly one refetch.
 *
 * `defaultLabel`, when given, marks the matching item's `default: true` —
 * at most one label, so at most one item across the whole upload (and thus
 * per chunk/batch, which is all the API allows) ever claims it.
 */
export const createFieldOptionsInChunks = async (
  createFieldOptions: CreateFieldOptionsTrigger,
  optionSetId: string,
  values: OptionDraftValue[],
  defaultLabel?: string | null,
): Promise<void> => {
  if (values.length === 0) return;

  const chunks = chunk(values, CREATE_OPTIONS_CHUNK_SIZE);

  const toPayload = (c: OptionDraftValue[]) =>
    c.map((value) => ({
      type: 'field_options' as const,
      attributes: {
        option_set_id: optionSetId,
        value: toFieldOptionValue(value),
        ...(defaultLabel != null &&
          toDefaultTrackingLabel(value) === defaultLabel && {
            default: true,
          }),
      },
    }));

  // All non-last chunks fire in parallel — no cache invalidation.
  if (chunks.length > 1) {
    const results = await Promise.allSettled(
      chunks.slice(0, -1).map((c) =>
        createFieldOptions({
          data: toPayload(c),
          skipInvalidation: true,
        }).unwrap(),
      ),
    );
    const firstFailure = results.find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    if (firstFailure) throw firstFailure.reason;
  }

  // Last chunk fires after all others succeed and triggers the cache refetch.
  await createFieldOptions({
    data: toPayload(chunks[chunks.length - 1]),
  }).unwrap();
};

/** TEXT/EMAIL/NUMBER/DATE options are single strings that fit the CSV pipeline's single-column shape. */
export type ScalarCsvLogicalType = 'TEXT' | 'EMAIL' | 'NUMBER' | 'DATE';

/** URL/MONETARY/ADDRESS/DURATION options are wire objects that use the CSV pipeline's multi-column shape (separate named columns per field). */
export type CompoundCsvLogicalType =
  | 'URL'
  | 'MONETARY'
  | 'ADDRESS'
  | 'DURATION';

/** Every logical type `parseCsvFile` accepts. */
export type CsvLogicalType = ScalarCsvLogicalType | CompoundCsvLogicalType;

const COMPOUND_CSV_LOGICAL_TYPES = new Set<CompoundCsvLogicalType>([
  'URL',
  'MONETARY',
  'ADDRESS',
  'DURATION',
]);

const isCompoundCsvLogicalType = (
  logicalType: CsvLogicalType,
): logicalType is CompoundCsvLogicalType =>
  COMPOUND_CSV_LOGICAL_TYPES.has(logicalType as CompoundCsvLogicalType);

/** Why a CSV row was rejected — a format error, or a duplicate against an existing option or an earlier row in the same file. */
export type CsvRowFailureReason =
  | 'DUPLICATE_EXISTING'
  | 'DUPLICATE_IN_FILE'
  | 'INVALID_AMOUNT'
  | 'INVALID_CURRENCY'
  | 'INVALID_DATE'
  | 'INVALID_EMAIL'
  | 'INVALID_NUMBER'
  | 'INVALID_PERIOD'
  | 'INVALID_URL';

/**
 * A single CSV row's outcome, keyed by its 1-based physical line number in
 * the file (counting the dropped header and blank lines, so "row N" matches
 * what the user sees opening the file in a spreadsheet). `raw` is always the
 * row's literal text (the single cell for a scalar row, or every compound
 * column joined with " | ") — what the preview modal renders, regardless of
 * outcome; `value` is the wire value actually created, present only when valid.
 */
export type CsvRowOutcome =
  | { row: number; status: 'valid'; value: OptionDraftValue; raw: string }
  | {
      row: number;
      status: 'invalid';
      reason: CsvRowFailureReason;
      raw: string;
    };

export type ParseCsvResult = {
  rowOutcomes: CsvRowOutcome[];
  truncated: boolean;
};

/** The values from every valid row, in file order — what actually gets created as options. */
export const getCsvImportValues = (
  result: ParseCsvResult,
): OptionDraftValue[] =>
  result.rowOutcomes.flatMap((outcome) =>
    outcome.status === 'valid' ? [outcome.value] : [],
  );

/** The row/reason pairs for every invalid row, in file order — what a results summary reports. */
export const getCsvImportFailures = (
  result: ParseCsvResult,
): { row: number; reason: CsvRowFailureReason }[] =>
  result.rowOutcomes.flatMap((outcome) =>
    outcome.status === 'invalid'
      ? [{ row: outcome.row, reason: outcome.reason }]
      : [],
  );

/**
 * Validates a single scalar option value's format — the same rule manual
 * add/edit-row entry (`AdvancedSettings`'s `getOptionValueError`) and CSV
 * import both apply, so the two paths can't quietly drift apart. `null`
 * means the value is valid. Stays intl-free (returns a reason code, not a
 * message) — callers format it via `csvRowFailureMessages.tsx`.
 */
export const validateScalarOptionFormat = (
  logicalType: ScalarCsvLogicalType,
  trimmedValue: string,
): CsvRowFailureReason | null => {
  if (logicalType === 'EMAIL' && !validateEmail(trimmedValue)) {
    return 'INVALID_EMAIL';
  }
  if (logicalType === 'NUMBER' && !Number.isFinite(Number(trimmedValue))) {
    return 'INVALID_NUMBER';
  }
  if (logicalType === 'DATE' && !DATE_ONLY_REGEX.test(trimmedValue)) {
    return 'INVALID_DATE';
  }
  return null;
};

/** Thrown when a CSV is not valid UTF-8 (e.g. UTF-16 with a BOM). */
export class UnsupportedCsvEncodingError extends Error {
  constructor() {
    super('Unsupported CSV encoding');
    this.name = 'UnsupportedCsvEncodingError';
  }
}

/** Thrown when a compound-type CSV's header row is missing one of its required columns (e.g. no `Currency` column for a Monetary field). */
export class MissingCsvColumnsError extends Error {
  constructor(public readonly missingColumns: string[]) {
    super(`Missing required CSV columns: ${missingColumns.join(', ')}`);
    this.name = 'MissingCsvColumnsError';
  }
}

const hasPrefix = (bytes: Uint8Array, prefix: readonly number[]): boolean =>
  bytes.length >= prefix.length && prefix.every((b, i) => bytes[i] === b);

/**
 * Decodes CSV bytes as UTF-8. UTF-8 with a BOM is accepted (Excel writes
 * one); UTF-16/32 BOMs, NUL bytes (typical of UTF-16 of ASCII), and any
 * other non-UTF-8 sequence are rejected so garbled content never reaches
 * preview or import.
 */
export const decodeUtf8CsvText = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  if (
    hasPrefix(bytes, [0xff, 0xfe]) ||
    hasPrefix(bytes, [0xfe, 0xff]) ||
    hasPrefix(bytes, [0x00, 0x00, 0xfe, 0xff]) ||
    bytes.includes(0)
  ) {
    throw new UnsupportedCsvEncodingError();
  }
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    throw new UnsupportedCsvEncodingError();
  }
};

/**
 * Parses column A from a scalar-type CSV, capped at 10 000 candidate rows
 * (or `maxRows`, for a smaller preview). Drops a leading "Value" header
 * (matching the downloadable import template) so re-uploading it doesn't
 * create a literal "Value" option.
 *
 * Every remaining row gets a `CsvRowOutcome`: a format check
 * (`validateScalarOptionFormat`) first, so an invalid-format duplicate is
 * reported as invalid-format rather than as a duplicate, then a dedup pass
 * (`deduplicateCsvLabels`) against both `existingLabels` and earlier rows in
 * the same file. Row numbers are 1-based physical line numbers in the file
 * (`skipEmptyLines: false`, tracked manually), including the dropped header
 * and blank lines, so "row N" matches what the user sees opening the file in
 * a spreadsheet.
 */
const parseScalarCsvText = (
  text: string,
  logicalType: ScalarCsvLogicalType,
  existingLabels: string[],
  maxRows: number,
): Promise<ParseCsvResult> =>
  new Promise((resolve, reject) => {
    const invalidOutcomes: CsvRowOutcome[] = [];
    const formatValid: { row: number; value: string }[] = [];
    let truncated = false;
    let candidateCount = 0;
    let physicalLine = 0;
    let isFirstCandidate = true;
    Papa.parse<string[]>(text, {
      skipEmptyLines: false,
      step: (result, parser) => {
        physicalLine += 1;
        const raw = result.data[0]?.trim();
        if (!raw) return;
        const isHeaderRow = isFirstCandidate && raw.toLowerCase() === 'value';
        isFirstCandidate = false;
        if (isHeaderRow) return;

        candidateCount += 1;
        const formatError =
          logicalType === 'TEXT'
            ? null
            : validateScalarOptionFormat(logicalType, raw);
        if (formatError) {
          invalidOutcomes.push({
            row: physicalLine,
            status: 'invalid',
            reason: formatError,
            raw,
          });
        } else {
          formatValid.push({ row: physicalLine, value: raw });
        }

        if (candidateCount >= maxRows) {
          truncated = maxRows >= CSV_ROW_LIMIT;
          parser.abort();
        }
      },
      complete: () => {
        const { rejected } = deduplicateCsvLabels(
          formatValid.map((candidate) => candidate.value),
          existingLabels,
        );
        const rejectedByIndex = new Map(
          rejected.map((r) => [r.index, r.reason]),
        );
        const dedupOutcomes: CsvRowOutcome[] = formatValid.map(
          (candidate, index) => {
            const reason = rejectedByIndex.get(index);
            return reason
              ? {
                  row: candidate.row,
                  status: 'invalid' as const,
                  reason,
                  raw: candidate.value,
                }
              : {
                  row: candidate.row,
                  status: 'valid' as const,
                  value: candidate.value,
                  raw: candidate.value,
                };
          },
        );
        const rowOutcomes = [...invalidOutcomes, ...dedupOutcomes].sort(
          (a, b) => a.row - b.row,
        );
        resolve({ rowOutcomes, truncated });
      },
      error: (err: Error) => reject(err),
    });
  });

/**
 * Fixed column names per compound type, matching each type's manual-entry
 * field labels — used both for the multi-column CSV parse and for building
 * `getFieldValuesCsvTemplate`'s header row.
 */
const COMPOUND_CSV_COLUMNS: Record<CompoundCsvLogicalType, string[]> = {
  URL: ['URL', 'Label'],
  MONETARY: ['Amount', 'Currency'],
  DURATION: ['Amount', 'Period'],
  ADDRESS: ['Address Line', 'City', 'State/Province', 'Postal Code', 'Country'],
};

/** Columns a compound row must have *some* text in to be a candidate row at all — Label is optional on a URL row, and no single ADDRESS column is required (only that at least one of the five is filled in). */
const COMPOUND_CSV_REQUIRED_COLUMNS: Record<CompoundCsvLogicalType, string[]> =
  {
    URL: ['URL'],
    MONETARY: ['Amount', 'Currency'],
    DURATION: ['Amount', 'Period'],
    ADDRESS: [],
  };

const normalizeCsvHeader = (header: string): string =>
  header.trim().toLowerCase();

/** Reads just the first physical line as raw header cells, without papaparse's `header: true` renaming a repeated column. */
const readCsvHeaderRow = (text: string): string[] => {
  const [firstLine = ''] = text.split(/\r?\n/, 1);
  const { data } = Papa.parse<string[]>(firstLine, { header: false });
  return (data[0] ?? []).map((header) => header.trim());
};

/** Maps each canonical column name (e.g. "Amount") to whatever header text the file actually used for it, matched case-insensitively so a hand-typed header isn't rejected over casing alone. */
const buildColumnAliasMap = (actualHeaders: string[]): Map<string, string> =>
  new Map(actualHeaders.map((header) => [normalizeCsvHeader(header), header]));

const findMissingCompoundColumns = (
  logicalType: CompoundCsvLogicalType,
  columnAliasMap: Map<string, string>,
): string[] =>
  COMPOUND_CSV_REQUIRED_COLUMNS[logicalType].filter(
    (column) => !columnAliasMap.has(normalizeCsvHeader(column)),
  );

/** Resolves one row's cells into a plain object keyed by canonical column name (e.g. `columns.Amount`), regardless of the file's actual header casing. */
const resolveCompoundColumns = (
  logicalType: CompoundCsvLogicalType,
  record: Record<string, string>,
  columnAliasMap: Map<string, string>,
): Record<string, string> => {
  const columns: Record<string, string> = {};
  for (const column of COMPOUND_CSV_COLUMNS[logicalType]) {
    const actualHeader = columnAliasMap.get(normalizeCsvHeader(column));
    columns[column] =
      (actualHeader ? record[actualHeader] : undefined)?.trim() ?? '';
  }
  return columns;
};

const URL_TEST_REGEXP = new RegExp(URL_REGEXP, 'i');

type CompoundRowParseResult =
  | { status: 'blank' }
  | { status: 'valid'; value: Record<string, unknown> }
  | { status: 'invalid'; reason: CsvRowFailureReason };

const parseUrlOptionRow = (
  columns: Record<string, string>,
): CompoundRowParseResult => {
  const url = columns.URL ?? '';
  const label = columns.Label ?? '';
  if (!url && !label) return { status: 'blank' };
  if (!URL_TEST_REGEXP.test(url))
    return { status: 'invalid', reason: 'INVALID_URL' };
  return {
    status: 'valid',
    value: label ? { value: url, label } : { value: url },
  };
};

const MONETARY_CURRENCIES = new Set<string>(Object.values(CurrencyType));

const parseMonetaryOptionRow = (
  columns: Record<string, string>,
): CompoundRowParseResult => {
  const amountText = columns.Amount ?? '';
  const currencyText = columns.Currency ?? '';
  if (!amountText && !currencyText) return { status: 'blank' };
  const amount = Number(amountText);
  if (!amountText || !Number.isFinite(amount)) {
    return { status: 'invalid', reason: 'INVALID_AMOUNT' };
  }
  const currency = currencyText.toUpperCase();
  if (!MONETARY_CURRENCIES.has(currency)) {
    return { status: 'invalid', reason: 'INVALID_CURRENCY' };
  }
  return { status: 'valid', value: { amount, currency } };
};

/** Singular/plural period tokens, case-insensitive, derived from `DurationInput.PERIODS` so the two lists can't drift apart. */
const DURATION_PERIOD_BY_TOKEN: Record<string, DurationPeriod> = PERIODS.reduce(
  (tokens, period) => {
    tokens[period] = period;
    tokens[`${period}s`] = period;
    return tokens;
  },
  {} as Record<string, DurationPeriod>,
);

const parseDurationOptionRow = (
  columns: Record<string, string>,
): CompoundRowParseResult => {
  const amountText = columns.Amount ?? '';
  const periodText = columns.Period ?? '';
  if (!amountText && !periodText) return { status: 'blank' };
  const amount = Number(amountText);
  if (!amountText || !Number.isInteger(amount) || amount <= 0) {
    return { status: 'invalid', reason: 'INVALID_AMOUNT' };
  }
  const period = DURATION_PERIOD_BY_TOKEN[periodText.toLowerCase()];
  if (!period) {
    return { status: 'invalid', reason: 'INVALID_PERIOD' };
  }
  return { status: 'valid', value: { amount, period } };
};

/**
 * No format checks — manual entry (`AddressInput`) has none either, so a
 * CSV row shouldn't hold compound-type CSV import to a stricter bar. A row
 * is only rejected here (as blank, not invalid) when every column is empty.
 */
const parseAddressOptionRow = (
  columns: Record<string, string>,
): CompoundRowParseResult => {
  const addressLine = columns['Address Line'] ?? '';
  const city = columns.City ?? '';
  const state = columns['State/Province'] ?? '';
  const postalCode = columns['Postal Code'] ?? '';
  const country = columns.Country ?? '';
  if (!addressLine && !city && !state && !postalCode && !country) {
    return { status: 'blank' };
  }
  return {
    status: 'valid',
    value: {
      address_lines: [addressLine],
      locality: city,
      administrative_area: state,
      postal_code: postalCode,
      country,
    },
  };
};

const parseCompoundOptionRow = (
  logicalType: CompoundCsvLogicalType,
  columns: Record<string, string>,
): CompoundRowParseResult => {
  if (logicalType === 'URL') return parseUrlOptionRow(columns);
  if (logicalType === 'MONETARY') return parseMonetaryOptionRow(columns);
  if (logicalType === 'DURATION') return parseDurationOptionRow(columns);
  return parseAddressOptionRow(columns);
};

/**
 * Parses a compound-type CSV using `header: true` (an exact header row is
 * required, matched case-insensitively) — separate named columns per field
 * (e.g. `Amount`/`Currency`), not the scalar pipeline's single unnamed
 * column. Rejects the whole file via `MissingCsvColumnsError` when a
 * required column's header is missing entirely (every row would otherwise
 * silently look blank); otherwise every row gets a `CsvRowOutcome` the same
 * way `parseScalarCsvText` does — format check, then dedup against
 * `existingLabels` (tracking labels, from `toDefaultTrackingLabel`) and
 * earlier rows in the file.
 *
 * A row whose first column starts with `#` is treated as a comment (skipped,
 * no outcome) — this is what lets the downloadable Monetary/Duration
 * templates ship a `# Valid currencies: ...`/`# Valid periods: ...` hint row
 * that survives a re-upload of the template unmodified.
 */
const parseCompoundCsvText = (
  text: string,
  logicalType: CompoundCsvLogicalType,
  existingLabels: string[],
  maxRows: number,
): Promise<ParseCsvResult> =>
  new Promise((resolve, reject) => {
    const columnAliasMap = buildColumnAliasMap(readCsvHeaderRow(text));
    const missingColumns = findMissingCompoundColumns(
      logicalType,
      columnAliasMap,
    );
    if (missingColumns.length > 0) {
      reject(new MissingCsvColumnsError(missingColumns));
      return;
    }

    const columnNames = COMPOUND_CSV_COLUMNS[logicalType];
    const invalidOutcomes: CsvRowOutcome[] = [];
    const formatValid: {
      row: number;
      value: Record<string, unknown>;
      raw: string;
    }[] = [];
    let truncated = false;
    let candidateCount = 0;
    let physicalLine = 1; // the header row itself is physical line 1

    Papa.parse<Record<string, string>>(text, {
      header: true,
      skipEmptyLines: false,
      step: (result, parser) => {
        physicalLine += 1;
        const columns = resolveCompoundColumns(
          logicalType,
          result.data,
          columnAliasMap,
        );
        const raw = columnNames.map((name) => columns[name] ?? '').join(' | ');

        if ((columns[columnNames[0]] ?? '').startsWith('#')) return;

        const parsed = parseCompoundOptionRow(logicalType, columns);
        if (parsed.status === 'blank') return;

        candidateCount += 1;
        if (parsed.status === 'invalid') {
          invalidOutcomes.push({
            row: physicalLine,
            status: 'invalid',
            reason: parsed.reason,
            raw,
          });
        } else {
          formatValid.push({ row: physicalLine, value: parsed.value, raw });
        }

        if (candidateCount >= maxRows) {
          truncated = maxRows >= CSV_ROW_LIMIT;
          parser.abort();
        }
      },
      complete: () => {
        const { rejected } = deduplicateCsvLabels(
          formatValid.map((candidate) =>
            toDefaultTrackingLabel(candidate.value),
          ),
          existingLabels,
        );
        const rejectedByIndex = new Map(
          rejected.map((r) => [r.index, r.reason]),
        );
        const dedupOutcomes: CsvRowOutcome[] = formatValid.map(
          (candidate, index) => {
            const reason = rejectedByIndex.get(index);
            return reason
              ? {
                  row: candidate.row,
                  status: 'invalid' as const,
                  reason,
                  raw: candidate.raw,
                }
              : {
                  row: candidate.row,
                  status: 'valid' as const,
                  value: candidate.value,
                  raw: candidate.raw,
                };
          },
        );
        const rowOutcomes = [...invalidOutcomes, ...dedupOutcomes].sort(
          (a, b) => a.row - b.row,
        );
        resolve({ rowOutcomes, truncated });
      },
      error: (err: Error) => reject(err),
    });
  });

/**
 * Parses a CSV of option values for `logicalType` — scalar types
 * (TEXT/EMAIL/NUMBER/DATE) via the single-column pipeline, compound types
 * (URL/MONETARY/ADDRESS/DURATION) via the multi-column pipeline. Capped at
 * 10 000 candidate rows (or `maxRows`, for a smaller preview).
 */
export const parseCsvFile = async (
  file: File,
  logicalType: CsvLogicalType,
  existingLabels: string[],
  maxRows: number = CSV_ROW_LIMIT,
): Promise<ParseCsvResult> => {
  const text = decodeUtf8CsvText(await file.arrayBuffer());
  return isCompoundCsvLogicalType(logicalType)
    ? parseCompoundCsvText(text, logicalType, existingLabels, maxRows)
    : parseScalarCsvText(text, logicalType, existingLabels, maxRows);
};

/** Filename for the downloadable CSV import template — shared across scalar and compound types. */
export const FIELD_VALUES_CSV_TEMPLATE_FILENAME =
  'field-values-import-template.csv';

/**
 * Builds the downloadable CSV import template for `logicalType`: the
 * single-column, no-header-required `Value` template for scalar types
 * (matches what `parseCsvFile`'s scalar branch expects — a header row for
 * clarity in a spreadsheet, then one value per line), or a one-example-row,
 * exact-header template per compound type. Monetary and Duration additionally
 * get a `#`-prefixed comment row listing every accepted enum value — the
 * closed set of currencies/periods isn't otherwise visible anywhere before
 * upload — rather than one example row per enum value, which for Monetary's
 * ~20 currencies would be unusable as an "example".
 */
export const getFieldValuesCsvTemplate = (
  logicalType: CsvLogicalType | null | undefined,
): string => {
  if (logicalType === 'URL') {
    return (
      Papa.unparse({
        fields: COMPOUND_CSV_COLUMNS.URL,
        data: [['https://example.com', 'Example']],
      }) + '\n'
    );
  }
  if (logicalType === 'MONETARY') {
    const currencies = [...MONETARY_CURRENCIES]
      .sort((a, b) => a.localeCompare(b))
      .join(', ');
    return (
      Papa.unparse({
        fields: COMPOUND_CSV_COLUMNS.MONETARY,
        data: [['100', 'USD']],
      }) + `\n# Valid currencies: ${currencies}\n`
    );
  }
  if (logicalType === 'DURATION') {
    return (
      Papa.unparse({
        fields: COMPOUND_CSV_COLUMNS.DURATION,
        data: [['2', 'weeks']],
      }) + `\n# Valid periods: ${PERIODS.join(', ')}\n`
    );
  }
  if (logicalType === 'ADDRESS') {
    return (
      Papa.unparse({
        fields: COMPOUND_CSV_COLUMNS.ADDRESS,
        data: [
          ['1600 Amphitheatre Pkwy', 'Mountain View', 'CA', '94043', 'US'],
        ],
      }) + '\n'
    );
  }
  return '﻿Value\nExample option 1\nExample option 2\n';
};

export type DeduplicateCsvLabelsResult = {
  kept: string[];
  rejected: {
    index: number;
    reason: 'DUPLICATE_EXISTING' | 'DUPLICATE_IN_FILE';
  }[];
};

/**
 * Splits csvLabels into those kept and those rejected as duplicates,
 * case-sensitively (matching the backend's (option_set_id, value)
 * uniqueness, which is a plain JSONB equality check — "Test" and "test" are
 * distinct, both-valid option values), against both existingLabels and
 * labels seen earlier in the CSV itself. First occurrence wins.
 */
export const deduplicateCsvLabels = (
  csvLabels: string[],
  existingLabels: string[],
): DeduplicateCsvLabelsResult => {
  const seen = new Set(existingLabels);
  const kept: string[] = [];
  const rejected: DeduplicateCsvLabelsResult['rejected'] = [];
  csvLabels.forEach((label, index) => {
    if (seen.has(label)) {
      rejected.push({
        index,
        reason: existingLabels.includes(label)
          ? 'DUPLICATE_EXISTING'
          : 'DUPLICATE_IN_FILE',
      });
      return;
    }
    seen.add(label);
    kept.push(label);
  });
  return { kept, rejected };
};

/**
 * TEXT, EMAIL, NUMBER, and DATE fields support a single free-form Default
 * Value (and, on Preset options, marking one option as the set's default).
 * Deliberately narrower than `supportsOptionSetEditor` — a default value
 * needs a single well-defined scalar to store (`toDefaultValueAttribute`,
 * and the default-option tracking below, are both string-only); URL and
 * MONETARY's compound values have no established default-value wire
 * contract yet.
 */
export const supportsDefaultValue = (
  logicalType: string | null | undefined,
): boolean =>
  logicalType === 'TEXT' ||
  logicalType === 'EMAIL' ||
  logicalType === 'NUMBER' ||
  logicalType === 'DATE';

/** Reads a free-form TEXT field's saved `default_value` (`{ data: [{ value }] }`) back out as plain text, for seeding the Edit panel's Default Value input. */
export const getFieldDefaultValueText = (
  field: Pick<ClientField, 'attributes'> | null,
): string => {
  const data = field?.attributes.default_value?.data as
    | { value?: unknown }[]
    | undefined;
  const value = data?.[0]?.value;
  return typeof value === 'string' ? value : '';
};

/** Builds a free-form TEXT field's `default_value` attribute: the entered text, or explicit `null` to clear/omit it (a Preset options field's default comes from marking one of its options as the set's default instead). */
export const toDefaultValueAttribute = (
  text: string,
): { data: [{ value: string }] } | null => {
  const trimmed = text.trim();
  return trimmed ? { data: [{ value: trimmed }] } : null;
};

export type FieldCreationType =
  | 'TEXT'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'DATE'
  | 'MONETARY'
  | 'FILE'
  | 'USER'
  | 'EMAIL'
  | 'URL'
  | 'DURATION'
  | 'DEPARTMENT'
  | 'ADDRESS';

export type CreateFieldDraft = {
  custom_name: string;
  description: string;
  field_type: FieldCreationType;
  allow_multiple_values: boolean;
  allow_adding_new_options: boolean;
  default_value: string;
  option_set_id?: string;
  number_format?: NumberFormat;
  input_type?: TextInputType;
};

export const toCreateFieldPayload = ({
  custom_name,
  description,
  field_type,
  allow_multiple_values,
  allow_adding_new_options,
  default_value,
  option_set_id,
  number_format,
  input_type,
}: CreateFieldDraft) => {
  const base = {
    custom_name: custom_name.trim(),
    ...(description.trim() && { description: description.trim() }),
    ...(option_set_id && { option_set_id }),
  };

  switch (field_type) {
    case 'TEXT':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'TEXT' as const,
          default_value: toDefaultValueAttribute(default_value),
          ...buildCustomSettingsAttributes(
            'TEXT',
            allow_multiple_values,
            allow_adding_new_options,
            undefined,
            undefined,
            option_set_id ? undefined : input_type,
          ),
        },
      };
    case 'FILE':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'FILE' as const,
          ...buildCustomSettingsAttributes(
            'FILE',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'USER':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'USER' as const,
          ...buildCustomSettingsAttributes(
            'USER',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'EMAIL':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'EMAIL' as const,
          default_value: toDefaultValueAttribute(default_value),
          ...buildCustomSettingsAttributes(
            'EMAIL',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    case 'URL':
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: 'URL' as const,
          ...buildCustomSettingsAttributes(
            'URL',
            allow_multiple_values,
            allow_adding_new_options,
          ),
        },
      };
    default:
      return {
        type: 'client_fields' as const,
        attributes: {
          ...base,
          logical_type: field_type as LogicalType,
          ...(supportsDefaultValue(field_type) && {
            default_value: toDefaultValueAttribute(default_value),
          }),
          ...buildCustomSettingsAttributes(
            field_type,
            allow_multiple_values,
            allow_adding_new_options,
            undefined,
            number_format,
          ),
        },
      };
  }
};

// --- Preset options: which option is the set's default ---
//
// Tracked by label (unique across existing + new options, same guarantee CSV
// import already relies on via `deduplicateCsvLabels`) rather than id, since a
// newly-added option has no id until it's actually created. Shared by
// AddFieldPanel (create) and EditFieldPanel (edit) so both panels' option
// list handlers (delete/edit/bulk-delete/toggle) apply the same rule.

/**
 * Extracts the label an `OptionDraftValue` participates in default-option
 * tracking under — a plain string is its own label. A compound value (URL,
 * MONETARY, ADDRESS, DURATION) has no natural string identity, so it's
 * canonicalized to a JSON string with keys sorted: two values built from the
 * same fields in a different order (e.g. a fresh add vs. one round-tripped
 * through an edit) still compare equal.
 */
export const toDefaultTrackingLabel = (value: OptionDraftValue): string =>
  typeof value === 'string'
    ? value
    : JSON.stringify(value, Object.keys(value).sort());

/** Clears `currentDefault` if `removedLabel` is the option being removed. */
export const clearDefaultOptionIfRemoved = (
  currentDefault: string | null,
  removedLabel: string | undefined,
): string | null =>
  removedLabel != null && removedLabel === currentDefault
    ? null
    : currentDefault;

/** Clears `currentDefault` if it's among the options being bulk-removed. */
export const clearDefaultOptionIfAnyRemoved = (
  currentDefault: string | null,
  removedLabels: (string | undefined)[],
): string | null =>
  currentDefault != null && removedLabels.includes(currentDefault)
    ? null
    : currentDefault;

/** Carries `currentDefault` over to `newLabel` when the option it pointed at (`oldLabel`) was just renamed. */
export const renameDefaultOption = (
  currentDefault: string | null,
  oldLabel: string | undefined,
  newLabel: string,
): string | null =>
  oldLabel != null && oldLabel === currentDefault ? newLabel : currentDefault;

/** Toggles `label`'s default status: default if it wasn't, cleared if it already was. */
export const toggleDefaultOption = (
  currentDefault: string | null,
  label: string,
): string | null => (label === currentDefault ? null : label);

/** Reads an option set's current default (if any) off a loaded page of its field options. */
export const getServerDefaultOption = (
  options: FieldOption[],
  logicalType?: string | null,
  formatNumber?: ReturnType<typeof useFormatNumber>,
  intl?: IntlShape,
): { id: string; label: string } | null => {
  const serverDefault = options.find((o) => o.attributes.default);
  return serverDefault
    ? {
        id: serverDefault.id,
        label: getFieldOptionLabel(
          serverDefault,
          logicalType,
          formatNumber,
          intl,
        ),
      }
    : null;
};

export type DefaultOptionAction =
  | { type: 'none' }
  | { type: 'promote'; optionId: string }
  | { type: 'demote'; optionId: string };

/**
 * Decides what (if anything) needs to change server-side for an option set's
 * default, once only *existing* options are in play — a default that
 * resolves to one of `optionsToCreate` needs no separate action here: it's
 * sent as `default: true` on creation instead, which the backend already
 * treats as demoting whatever was previously default.
 *
 * `existingDefaultOptionId` is the id of whichever *existing* option
 * `defaultOption` currently refers to (or null if it refers to a
 * not-yet-created option, or nothing) — tracked directly by the caller as
 * the user picks/renames/removes options, rather than looked up here by
 * label against a loaded page of options. Option sets can hold tens of
 * thousands of options, so the panel only ever loads a search-scoped page of
 * them; looking the label up against that page here would silently produce
 * the wrong answer (e.g. a false "not found") whenever a search filter
 * happens to be active at submit time.
 */
export const resolveDefaultOptionAction = ({
  defaultOption,
  originalDefaultOptionId,
  existingDefaultOptionId,
  deletedOptionIds,
  optionsToCreate,
}: {
  defaultOption: string | null;
  originalDefaultOptionId: string | null;
  existingDefaultOptionId: string | null;
  deletedOptionIds: string[];
  optionsToCreate: string[];
}): DefaultOptionAction => {
  if (defaultOption != null && optionsToCreate.includes(defaultOption)) {
    return { type: 'none' };
  }

  const deleted = new Set(deletedOptionIds);
  const newDefaultId =
    existingDefaultOptionId && !deleted.has(existingDefaultOptionId)
      ? existingDefaultOptionId
      : null;

  if (newDefaultId && newDefaultId !== originalDefaultOptionId) {
    return { type: 'promote', optionId: newDefaultId };
  }
  if (
    !newDefaultId &&
    originalDefaultOptionId &&
    !deleted.has(originalDefaultOptionId)
  ) {
    return { type: 'demote', optionId: originalDefaultOptionId };
  }
  return { type: 'none' };
};

const DELETE_OPTIONS_CHUNK_SIZE = 100;

type DeleteFieldOptionsTrigger = (arg: {
  ids: string[];
  option_set_id: string;
}) => {
  unwrap: () => Promise<unknown>;
};

/** Deletes field options in batches of the API's 100-id limit. */
export const deleteFieldOptionsInChunks = async (
  deleteFieldOptions: DeleteFieldOptionsTrigger,
  optionSetId: string,
  ids: string[],
) => {
  for (const batch of chunk(ids, DELETE_OPTIONS_CHUNK_SIZE)) {
    await deleteFieldOptions({
      ids: batch,
      option_set_id: optionSetId,
    }).unwrap();
  }
};

/** Drops re-added drafts that match an option staged for deletion, so the existing option is kept instead of deleted and recreated. */
export const reconcileReaddedOptions = ({
  logicalType,
  existingOptions,
  deletedOptionIds,
  deleteAllExistingOptions,
  optionsToCreate,
  defaultOption,
  existingDefaultOptionId,
}: {
  logicalType: string | null | undefined;
  existingOptions: FieldOption[];
  deletedOptionIds: string[];
  deleteAllExistingOptions: boolean;
  optionsToCreate: OptionDraftValue[];
  defaultOption: string | null;
  existingDefaultOptionId: string | null;
}) => {
  const deleted = new Set(deletedOptionIds);
  const candidatesByKey = new Map<string, string[]>();
  existingOptions.forEach((option) => {
    if (!deleteAllExistingOptions && !deleted.has(option.id)) return;
    const key = toDefaultTrackingLabel(
      trimOptionDraftValue(getExistingOptionValue(logicalType, option)),
    );
    candidatesByKey.set(key, [...(candidatesByKey.get(key) ?? []), option.id]);
  });

  const keptIds = new Set<string>();
  let defaultOptionId = existingDefaultOptionId;
  const remainingToCreate = optionsToCreate.filter((draft) => {
    const key = toDefaultTrackingLabel(trimOptionDraftValue(draft));
    const matchedId = candidatesByKey.get(key)?.shift();
    if (!matchedId) return true;
    keptIds.add(matchedId);
    if (defaultOption != null && key === defaultOption) {
      defaultOptionId = matchedId;
    }
    return false;
  });

  if (keptIds.size === 0) {
    return {
      optionsToCreate,
      deletedOptionIds,
      deleteAllExistingOptions,
      defaultOptionId,
    };
  }

  const idsToDelete = deleteAllExistingOptions
    ? existingOptions.map((option) => option.id)
    : deletedOptionIds;
  return {
    optionsToCreate: remainingToCreate,
    deletedOptionIds: idsToDelete.filter((id) => !keptIds.has(id)),
    deleteAllExistingOptions: false,
    defaultOptionId,
  };
};

export const buildFormFooterActions = ({
  cancelLabel,
  submitLabel,
  isSubmitting,
  disabled = false,
  tooltip,
  onCancel,
  onSubmit,
}: {
  cancelLabel: string;
  submitLabel: string;
  isSubmitting: boolean;
  /** Additional condition (beyond `isSubmitting`) disabling the primary action — e.g. an incomplete preset-options field. */
  disabled?: boolean;
  tooltip?: string;
  onCancel: () => void;
  onSubmit: () => void;
}): Action[] => [
  {
    label: cancelLabel,
    text: cancelLabel,
    variant: 'tertiary',
    onClick: onCancel,
  },
  {
    label: submitLabel,
    text: submitLabel,
    variant: 'primary',
    isLoading: isSubmitting,
    disabled: isSubmitting || disabled,
    tooltip: tooltip ? { text: tooltip } : undefined,
    onClick: onSubmit,
  },
];
